import { useState, useRef, useEffect, useCallback } from "react";
import {
  Alert,
  Avatar,
  Box,
  CircularProgress,
  Divider,
  IconButton,
  List,
  ListItem,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import SendIcon from "@mui/icons-material/Send";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "../lib/supabase";
import { useAuthStore } from "../stores/auth";
import { formatDistanceToNow } from "date-fns";

interface CommentRow {
  id: number;
  entity_type: string;
  entity_id: string;
  body: string;
  mentioned_user_ids: string[];
  created_at: string;
  updated_at: string;
  author_id: string;
  author_name: string | null;
  author_email: string | null;
}

function commentKeys(entityType: string, entityId: string) {
  return ["comments", entityType, entityId] as const;
}

function initials(name: string | null, email: string | null): string {
  if (name) {
    const parts = name.trim().split(/\s+/).filter(Boolean);
    if (parts.length >= 2) {
      return ((parts[0]?.[0] ?? "") + (parts[parts.length - 1]?.[0] ?? "")).toUpperCase();
    }
    return (parts[0] ?? "").slice(0, 2).toUpperCase();
  }
  return (email ?? "?").slice(0, 2).toUpperCase();
}

function useComments(entityType: string, entityId: string) {
  return useQuery({
    queryKey: commentKeys(entityType, entityId),
    queryFn: async (): Promise<CommentRow[]> => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { data, error } = await (supabase as any)
        .from("manna_entity_comments_view")
        .select("*")
        .eq("entity_type", entityType)
        .eq("entity_id", entityId)
        .order("created_at", { ascending: true });
      if (error) throw error;
      return (data ?? []) as CommentRow[];
    },
    enabled: Boolean(entityType && entityId),
  });
}

export interface CommentThreadProps {
  entityType: "person" | "organization" | "deal" | "activity";
  entityId: string;
}

export function CommentThread({ entityType, entityId }: CommentThreadProps) {
  const qc = useQueryClient();
  const user = useAuthStore((s) => s.user);
  const [draft, setDraft] = useState("");
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editBody, setEditBody] = useState("");
  const [postError, setPostError] = useState<string | null>(null);
  const bottomRef = useRef<HTMLDivElement>(null);

  const { data: comments = [], isLoading } = useComments(entityType, entityId);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [comments.length]);

  const postMutation = useMutation({
    mutationFn: async (body: string) => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { error } = await (supabase.rpc as any)("manna_create_comment", {
        payload: {
          entity_type: entityType,
          entity_id: entityId,
          body,
          mentioned_user_ids: [],
        },
      });
      if (error) throw error;
    },
    onSuccess: () => {
      setDraft("");
      setPostError(null);
      qc.invalidateQueries({ queryKey: commentKeys(entityType, entityId) });
    },
    onError: (e: unknown) => {
      setPostError(e instanceof Error ? e.message : "Failed to post comment.");
    },
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, body }: { id: number; body: string }) => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { error } = await (supabase.rpc as any)("manna_update_comment", {
        p_id: id,
        p_body: body,
      });
      if (error) throw error;
    },
    onSuccess: () => {
      setEditingId(null);
      qc.invalidateQueries({ queryKey: commentKeys(entityType, entityId) });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: number) => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { error } = await (supabase.rpc as any)("manna_delete_comment", { p_id: id });
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: commentKeys(entityType, entityId) });
    },
  });

  const handlePost = useCallback(() => {
    const body = draft.trim();
    if (!body) return;
    postMutation.mutate(body);
  }, [draft, postMutation]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      handlePost();
    }
  };

  return (
    <Box>
      {isLoading ? (
        <Box display="flex" justifyContent="center" py={2}>
          <CircularProgress size={20} />
        </Box>
      ) : comments.length === 0 ? (
        <Typography variant="caption" color="text.secondary" sx={{ fontStyle: "italic" }}>
          No comments yet.
        </Typography>
      ) : (
        <List dense disablePadding sx={{ mb: 1 }}>
          {comments.map((c, i) => (
            <Box key={c.id}>
              {i > 0 && <Divider sx={{ my: 0.5 }} />}
              <ListItem disableGutters alignItems="flex-start" sx={{ gap: 1, py: 0.75 }}>
                <Avatar sx={{ width: 26, height: 26, fontSize: 11, mt: 0.25, flexShrink: 0 }}>
                  {initials(c.author_name, c.author_email)}
                </Avatar>
                <Box flex={1} minWidth={0}>
                  <Stack direction="row" alignItems="baseline" spacing={1} sx={{ mb: 0.25 }}>
                    <Typography variant="caption" fontWeight={600}>
                      {c.author_name ?? c.author_email ?? "Unknown"}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {formatDistanceToNow(new Date(c.created_at), { addSuffix: true })}
                    </Typography>
                    {c.updated_at !== c.created_at && (
                      <Typography variant="caption" color="text.disabled">
                        (edited)
                      </Typography>
                    )}
                  </Stack>

                  {editingId === c.id ? (
                    <Stack spacing={0.5}>
                      <TextField
                        size="small"
                        multiline
                        fullWidth
                        value={editBody}
                        onChange={(e) => setEditBody(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
                            e.preventDefault();
                            updateMutation.mutate({ id: c.id, body: editBody });
                          }
                          if (e.key === "Escape") setEditingId(null);
                        }}
                        autoFocus
                      />
                      <Stack direction="row" spacing={0.5}>
                        <IconButton
                          size="small"
                          color="primary"
                          aria-label="Save edit"
                          disabled={!editBody.trim() || updateMutation.isPending}
                          onClick={() => updateMutation.mutate({ id: c.id, body: editBody })}
                        >
                          <CheckIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          aria-label="Cancel edit"
                          onClick={() => setEditingId(null)}
                        >
                          <CloseIcon fontSize="small" />
                        </IconButton>
                      </Stack>
                    </Stack>
                  ) : (
                    <Typography
                      variant="body2"
                      sx={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}
                    >
                      {c.body}
                    </Typography>
                  )}
                </Box>

                {c.author_id === user?.id && editingId !== c.id && (
                  <Stack direction="row" sx={{ flexShrink: 0 }}>
                    <Tooltip title="Edit">
                      <IconButton
                        size="small"
                        aria-label="Edit comment"
                        sx={{ p: 0.25 }}
                        onClick={() => {
                          setEditingId(c.id);
                          setEditBody(c.body);
                        }}
                      >
                        <EditIcon sx={{ fontSize: 14 }} />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton
                        size="small"
                        color="error"
                        aria-label="Delete comment"
                        sx={{ p: 0.25 }}
                        disabled={deleteMutation.isPending}
                        onClick={() => deleteMutation.mutate(c.id)}
                      >
                        <DeleteIcon sx={{ fontSize: 14 }} />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                )}
              </ListItem>
            </Box>
          ))}
          <div ref={bottomRef} />
        </List>
      )}

      {postError && (
        <Alert severity="error" onClose={() => setPostError(null)} sx={{ mb: 1 }}>
          {postError}
        </Alert>
      )}

      <Paper variant="outlined" sx={{ display: "flex", alignItems: "flex-end", gap: 0.5, p: 0.5 }}>
        <TextField
          size="small"
          multiline
          maxRows={4}
          fullWidth
          placeholder="Add a comment… (⌘↵ to send)"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={handleKeyDown}
          disabled={postMutation.isPending}
          sx={{ "& .MuiOutlinedInput-notchedOutline": { border: "none" } }}
        />
        <IconButton
          size="small"
          color="primary"
          aria-label="Post comment"
          disabled={!draft.trim() || postMutation.isPending}
          onClick={handlePost}
        >
          {postMutation.isPending ? <CircularProgress size={16} /> : <SendIcon fontSize="small" />}
        </IconButton>
      </Paper>
    </Box>
  );
}
